import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Play, RefreshCw, XCircle } from "lucide-react";

const DEFAULT_HOMEPAGE = "https://www.amasicurezza.com/";

export default function CatalogConnectivity() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [host, setHost] = useState("www.amasicurezza.com");
  const [homepage, setHomepage] = useState(DEFAULT_HOMEPAGE);
  const [productUrl, setProductUrl] = useState("");
  const [forceIpv4, setForceIpv4] = useState(true);
  const [connectT, setConnectT] = useState(20);
  const [readT, setReadT] = useState(30);

  const loadHistory = async () => {
    try { const r = await api.get("/catalog/connectivity-tests", { params: { limit: 10 } }); setHistory(r.data); }
    catch { /* silent */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/catalog/connectivity-test", {
        host, homepage_url: homepage, product_url: productUrl || null,
        force_ipv4: forceIpv4, connect_timeout_s: connectT, read_timeout_s: readT,
      }, { timeout: (connectT + readT + 30) * 1000 });
      setResult(r.data);
      if (r.data?.verdict?.success) toast.success("Collegamento riuscito dal runtime attuale.");
      else toast.error(r.data?.verdict?.message || "Test fallito.");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Errore esecuzione test");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-4" data-testid="catalog-connectivity-page">
      <Link to="/catalogo" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Catalogo prodotti</Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Catalogo → Diagnostica</div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#0a0a2e]">Test collegamento al sito AMA</h1>
        <p className="text-sm text-slate-500">Test di connettività stratificato: DNS → TCP:443 → TLS/SNI → HTTPS GET homepage → pagina prodotto opzionale. Eseguibile dal runtime di anteprima e da quello di produzione per confronto A/B.</p>
      </div>

      <Card className="ama-card border-0 shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Parametri</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Host</Label><Input data-testid="conn-host" value={host} onChange={(e) => setHost(e.target.value)} /></div>
          <div><Label>Homepage URL</Label><Input data-testid="conn-homepage" value={homepage} onChange={(e) => setHomepage(e.target.value)} /></div>
          <div className="md:col-span-2"><Label>URL prodotto pubblico (opzionale)</Label><Input data-testid="conn-product" placeholder="Es. https://www.amasicurezza.com/prodotto.php?alias=..." value={productUrl} onChange={(e) => setProductUrl(e.target.value)} /></div>
          <div><Label>Timeout connessione (s)</Label><Input type="number" min={5} max={60} value={connectT} onChange={(e) => setConnectT(+e.target.value || 20)} /></div>
          <div><Label>Timeout lettura (s)</Label><Input type="number" min={5} max={90} value={readT} onChange={(e) => setReadT(+e.target.value || 30)} /></div>
          <label className="flex items-center gap-2 text-sm mt-1"><input type="checkbox" checked={forceIpv4} onChange={(e) => setForceIpv4(e.target.checked)} /> Forza IPv4</label>
          <div className="flex justify-end">
            <Button data-testid="btn-run-connectivity" onClick={run} disabled={busy} className="ama-btn-primary">
              {busy ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Test in corso…</> : <><Play className="w-4 h-4 mr-1" /> Esegui test</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && <ResultCard result={result} />}

      <Card className="ama-card border-0 shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Ultimi test eseguiti</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 && <div className="text-sm text-slate-500 italic">Nessun test eseguito.</div>}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 uppercase tracking-wider bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-2">Timestamp</th>
                  <th className="text-left py-2 px-2">Ambiente</th>
                  <th className="text-left py-2 px-2">IP</th>
                  <th className="text-left py-2 px-2">DNS</th>
                  <th className="text-left py-2 px-2">TCP</th>
                  <th className="text-left py-2 px-2">TLS</th>
                  <th className="text-left py-2 px-2">HTTP</th>
                  <th className="text-left py-2 px-2">ms</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`conn-hist-row-${i}`}>
                    <td className="py-1.5 px-2 font-mono">{new Date(h.timestamp || h.created_at).toLocaleString("it-IT")}</td>
                    <td className="py-1.5 px-2"><Badge className={h?.runtime?.environment === "production" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}>{h?.runtime?.environment}</Badge></td>
                    <td className="py-1.5 px-2 font-mono">{(h.dns_a?.addresses || [])[0] || "—"}</td>
                    <td className="py-1.5 px-2"><Pill ok={h.dns_a?.ok} /></td>
                    <td className="py-1.5 px-2"><Pill ok={h.tcp?.ok} note={h.tcp?.error_code} /></td>
                    <td className="py-1.5 px-2"><Pill ok={h.tls?.ok} /></td>
                    <td className="py-1.5 px-2"><Pill ok={h.homepage?.ok} note={h.homepage?.http_status} /></td>
                    <td className="py-1.5 px-2 font-mono">{h.total_elapsed_ms || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {history.some((h) => h?.runtime?.environment === "preview") && history.some((h) => h?.runtime?.environment === "production") ? (
            <Alert className="mt-3 bg-emerald-50 border-emerald-200 text-emerald-800"><AlertTitle>Confronto A/B disponibile</AlertTitle><AlertDescription>Sono presenti test da entrambi gli ambienti — usa la tabella qui sopra per confrontare gli esiti.</AlertDescription></Alert>
          ) : (
            <div className="text-xs text-slate-500 mt-2">Esegui questo test dopo il deployment in produzione per confrontare i risultati con l'ambiente di anteprima.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Pill({ ok, note }) {
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {note != null ? String(note) : (ok ? "OK" : "KO")}
    </span>
  );
}

function ResultCard({ result }) {
  const v = result.verdict || {};
  return (
    <Card className="ama-card border-0 shadow-none" data-testid="connectivity-result">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg">Risultato — {result?.runtime?.environment}</CardTitle>
        {v.success ? <Badge className="bg-emerald-600 text-white">Riuscito</Badge> : <Badge className="bg-rose-600 text-white">Non riuscito</Badge>}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <KV k="Host" v={result.host} />
          <KV k="Runtime" v={result?.runtime?.backend_hostname?.slice(0, 20) || "—"} />
          <KV k="IP risolto" v={(result?.dns_a?.addresses || [])[0] || "—"} />
          <KV k="Elapsed totale" v={`${result.total_elapsed_ms} ms`} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Section title="1. DNS (A)" step={result.dns_a}>
            <KV k="Indirizzi" v={(result.dns_a?.addresses || []).join(", ") || "—"} />
            <KV k="Latenza" v={result.dns_a?.elapsed_ms ? `${result.dns_a.elapsed_ms} ms` : "—"} />
          </Section>
          <Section title="1b. DNS (AAAA / IPv6)" step={result.dns_aaaa} optional>
            <KV k="Indirizzi" v={(result.dns_aaaa?.addresses || []).join(", ") || "—"} />
          </Section>
          <Section title="2. TCP:443" step={result.tcp}>
            <KV k="IP:porta" v={`${result.tcp?.address || "—"}:${result.tcp?.port}`} />
            <KV k="Latenza" v={result.tcp?.elapsed_ms != null ? `${result.tcp.elapsed_ms} ms` : "—"} />
            <KV k="Errore" v={result.tcp?.error_code ? `${result.tcp.error_code} — ${result.tcp?.error || ""}` : "—"} />
          </Section>
          <Section title="3. TLS handshake" step={result.tls}>
            <KV k="SNI" v={result.tls?.sni} />
            <KV k="Protocollo" v={result.tls?.protocol || "—"} />
            <KV k="Cipher" v={result.tls?.cipher || "—"} />
            <KV k="Certificato" v={result.tls?.peer_cert_subject || "—"} />
            <KV k="Errore" v={result.tls?.error_code || "—"} />
          </Section>
          <Section title="4. HTTPS GET homepage" step={result.homepage}>
            <KV k="Status" v={result.homepage?.http_status ?? "—"} />
            <KV k="Server" v={result.homepage?.server_header || "—"} />
            <KV k="Content-Type" v={result.homepage?.content_type || "—"} />
            <KV k="Body length" v={result.homepage?.body_length ? `${result.homepage.body_length} B` : "—"} />
            <KV k="Latenza" v={result.homepage?.elapsed_ms != null ? `${result.homepage.elapsed_ms} ms` : "—"} />
            <KV k="Errore" v={result.homepage?.error_code || "—"} />
          </Section>
          <Section title="5. Pagina prodotto" step={result.product_page} optional>
            <KV k="URL" v={result.product_page?.url || "—"} />
            <KV k="Status" v={result.product_page?.http_status ?? "—"} />
            <KV k="Errore" v={result.product_page?.error_code || "—"} />
          </Section>
        </div>

        <Alert className={v.success ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}>
          <AlertTitle>Verdetto</AlertTitle>
          <AlertDescription>
            {v.message}
            {!v.success && result.tcp?.error_code === "CONNECT_TIMEOUT" && (
              <div className="mt-2 text-xs">
                <b>Diagnosi:</b> il problema è di connettività di rete tra il runtime AMA ServiceFlow ({result?.runtime?.environment}) e l'hosting di www.amasicurezza.com. DNS risolve regolarmente, ma la porta 443 dell'IP {(result?.dns_a?.addresses || [])[0]} non risponde entro il timeout. Non è un problema del crawler né della logica di estrazione prodotti.
              </div>
            )}
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}

function Section({ title, step, optional, children }) {
  const ok = !!step?.ok;
  return (
    <div className={`rounded-lg border p-3 ${ok ? "border-emerald-200 bg-emerald-50/40" : optional && !step?.error ? "border-slate-200 bg-slate-50" : "border-rose-200 bg-rose-50/40"}`}>
      <div className="flex items-center gap-2 mb-1">
        {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : (optional && !step?.error ? <span className="w-4 h-4 rounded-full border border-slate-300 inline-block" /> : <XCircle className="w-4 h-4 text-rose-600" />)}
        <div className="font-semibold text-[#0a0a2e] text-sm">{title}</div>
      </div>
      <div className="text-xs space-y-0.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{k}</span>
      <span className="font-mono text-slate-700 text-right break-all">{v || "—"}</span>
    </div>
  );
}
